/**
 * Contact capture — the ONE rule for contact details typed on a customer-facing
 * page (tv-2na.9.6 / tv-2na.9.8).
 *
 * Whoever opens a pay link may not be the customer (a partner or parent paying),
 * so a page never renames or re-phones a known customer. It only FILLS what the
 * record is missing:
 *  - phone: set only when blank (board 02: the phone is collected on payment so a
 *    number is on file);
 *  - name: set only when the record has no real name (blank or the IG-handle
 *    placeholder); a missing last name is added only when the typed first name
 *    matches the one on file;
 *  - email: set only when blank (D05-11, Anas 6 Oct: an email is mandatory when
 *    a deposit is taken, so the pay page collects one when none is on file).
 * A deliberate staff edit is the only overwrite.
 *
 * The WAIVER never uses this (DECIDED 24 Sep, Q7): the client fills the waiver and
 * it must never change the customer record; what they type stays on the signed
 * waiver (`signedIdentity`) only.
 */

/** The customer fields the capture rule reads. */
export interface ContactOnFile {
  firstName: string;
  lastName: string;
  phone?: string | null;
  email?: string | null;
}

/** What a customer-facing page captured. */
export interface CapturedContact {
  /** Full name as typed ("First Last …"). */
  name?: string | null;
  mobile?: string | null;
  email?: string | null;
}

/** The fields to write (empty when nothing is missing). */
export type ContactPatch = Partial<{ firstName: string; lastName: string; phone: string; email: string }>;

/** A usable email address: the ONE format rule for every form that takes one. */
export function isEmail(raw: string | null | undefined): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((raw ?? '').trim());
}

/** The name the system gives an IG DM lead before we know who they are
 *  (resolveInboundDmCustomer: `@handle`, or "Instagram DM" for a bad handle). */
export function isPlaceholderName(firstName: string, lastName: string): boolean {
  const first = firstName.trim();
  const last = lastName.trim();
  return !first || (first.startsWith('@') && !last) || (first === 'Instagram' && last === 'DM');
}

/** Fill only the empty fields of `existing` from `captured`; never overwrite. */
export function mergeCapturedContact(existing: ContactOnFile, captured: CapturedContact): ContactPatch {
  const [firstName, ...rest] = (captured.name ?? '').split(/\s+/).filter(Boolean);
  const lastName = rest.join(' ');
  const mobile = (captured.mobile ?? '').trim();
  const patch: ContactPatch = {};
  if (firstName && isPlaceholderName(existing.firstName, existing.lastName)) {
    patch.firstName = firstName;
    patch.lastName = lastName;
  } else if (
    lastName &&
    !existing.lastName.trim() &&
    firstName?.toLowerCase() === existing.firstName.trim().toLowerCase()
  ) {
    patch.lastName = lastName;
  }
  if (mobile && !(existing.phone ?? '').trim()) patch.phone = mobile;
  const email = (captured.email ?? '').trim().toLowerCase();
  if (email && isEmail(email) && !(existing.email ?? '').trim()) patch.email = email;
  return patch;
}

/**
 * A usable Australian phone number (B8, 75mx: a push to the Agent needs one): a
 * mobile (04xx xxx xxx) or a landline (0[2378] xxxx xxxx), with an optional
 * +61 / 61 in place of the leading 0 and spaces, dashes, dots or brackets.
 */
export function isAuPhone(raw: string | null | undefined): boolean {
  const digits = (raw ?? '').replace(/[\s\-().]/g, '');
  const local = digits.replace(/^\+?61/, '0');
  return /^0(4\d{8}|[2378]\d{8})$/.test(local);
}
